import {
  createElement,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

type HeadingTag = "h1" | "h2" | "h3" | "h4";

// Recipes lifted verbatim from the two Suisse-based reference pages:
// components/home-v3/* and components/hong-kong-company-formation-v2/*.
type HeadingVariant =
  | "hero" // page hero H1
  | "section" // standard section H2, has text-shadow
  | "sectionCentered" // centered/FAQ-style H2, wider sm leading, no shadow
  | "banner" // bold CTA-band H2, clamp-based
  | "cardTitle" // bold card/feature title
  | "cardTitleLight"; // regular-weight card/feature title

const HEADING_VARIANTS: Record<HeadingVariant, string> = {
  hero: "font-suisse-intl text-[34px] leading-[40px] sm:text-[48px] sm:leading-[54px] font-semibold text-center",
  section:
    "font-suisse-works text-3xl font-normal leading-[1.15] tracking-[-1.92px] text-white [text-shadow:0px_4px_24px_#0000000d] sm:text-4xl sm:leading-[54px]",
  sectionCentered:
    "font-suisse-works text-center text-4xl font-normal leading-[1.15] tracking-[-1.92px] text-white sm:text-5xl sm:leading-[68px]",
  banner:
    "font-suisse-works-bold text-center text-[clamp(24px,3.34vw,48px)] font-bold leading-[1.242] tracking-[0] text-white",
  cardTitle:
    "font-suisse-intl-bold text-2xl font-bold leading-7 tracking-[0] text-white",
  cardTitleLight:
    "font-suisse-intl text-2xl font-normal leading-[28.8px] tracking-[-0.24px] text-white",
};

interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  // Semantic tag is independent of visual variant — e.g. a page's only
  // <h1> may visually be a "section"-sized heading, not the hero recipe.
  as: HeadingTag;
  variant: HeadingVariant;
  children?: ReactNode;
}

export function Heading({
  as,
  variant,
  className,
  children,
  ...rest
}: HeadingProps) {
  return createElement(
    as,
    { className: cn(HEADING_VARIANTS[variant], className), ...rest },
    children
  );
}

type TextVariant = "body" | "muted";
type TextSize = "base" | "sm" | "xs";

const TEXT_SIZES: Record<TextSize, string> = {
  base: "text-base leading-6",
  sm: "text-sm leading-5",
  xs: "text-xs leading-4",
};

interface TextProps extends HTMLAttributes<HTMLParagraphElement> {
  as?: ElementType;
  variant?: TextVariant;
  size?: TextSize;
  muted?: 60 | 70;
  children?: ReactNode;
}

export function Text({
  as: Tag = "p",
  variant = "body",
  size = "base",
  muted = 70,
  className,
  children,
  ...rest
}: TextProps) {
  return (
    <Tag
      className={cn(
        "font-suisse-intl font-normal tracking-[0]",
        TEXT_SIZES[size],
        variant === "muted" ? `text-white/${muted}` : "text-white",
        className
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
